import { createSignal, createMemo, For, Show } from "solid-js";
import type { RealtimeConnectionInfo } from "../types";
import { KNOWN_EVENT_TYPES, EVENT_CATEGORIES } from "@searchgrid/shared/realtime/filter";
import { ExpandablePayload, DirectionIndicator, CopyButton } from "./EventHelpers";

function exportJSON(data: any, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ConnectionDetail(props: {
  connection: RealtimeConnectionInfo;
  allEvents: any[];
  onClose: () => void;
}) {
  const [selectedTypes, setSelectedTypes] = createSignal<Set<string>>(new Set());
  const [sortDir, setSortDir] = createSignal<"desc" | "asc">("desc");

  const connEvents = createMemo(() => {
    let evts = props.allEvents.filter((e: any) =>
      e.sender?.connectionId === props.connection.connectionId
    );
    if (selectedTypes().size > 0) {
      evts = evts.filter((e: any) => selectedTypes().has(e.original?.type));
    }
    evts = [...evts].sort((a: any, b: any) =>
      sortDir() === "desc" ? b.timestamp - a.timestamp : a.timestamp - b.timestamp
    );
    return evts;
  });

  function toggleType(type: string) {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  function selectCategory(types: readonly string[]) {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      const allSelected = types.every((t) => next.has(t));
      if (allSelected) {
        for (const t of types) next.delete(t);
      } else {
        for (const t of types) next.add(t);
      }
      return next;
    });
  }

  const c = props.connection;

  return (
    <div class="border border-cyan-900/50 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between bg-gray-900 px-4 py-2 border-b border-gray-800">
        <div class="flex items-center gap-3 text-xs">
          <span class="font-mono text-cyan-400">{c.connectionId?.slice(0, 12)}</span>
          <span class={`${c.role === "searcher" ? "text-cyan-400" : c.role === "manager" ? "text-purple-400" : "text-gray-400"}`}>{c.role}</span>
          <span class="text-gray-500">{c.projectId?.slice(0, 10)}</span>
          <span class="text-gray-500">{c.userId?.slice(0, 10)}</span>
          <span class="text-gray-500">{new Date(c.connectedAt).toLocaleTimeString()}</span>
          <span class="text-gray-600">({connEvents().length} events)</span>
        </div>
        <div class="flex items-center gap-2">
          <button
            onClick={() => exportJSON(connEvents(), `connection-${c.connectionId.slice(0, 12)}.json`)}
            class="px-2 py-1 text-[10px] bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer"
          >
            Export
          </button>
          <button onClick={props.onClose} class="text-gray-500 hover:text-gray-200 cursor-pointer text-sm">x</button>
        </div>
      </div>

      <div class="px-4 py-2 border-b border-gray-800/50 flex items-center gap-2 flex-wrap text-[10px]">
        <span class="text-gray-500">Filter:</span>
        <For each={Object.entries(EVENT_CATEGORIES)}>
          {([cat, types]) => (
            <button
              onClick={() => selectCategory(types)}
              class={`px-1.5 py-0.5 rounded cursor-pointer ${
                types.every((t) => selectedTypes().has(t)) ? "bg-cyan-900 text-cyan-300" : "bg-gray-800 text-gray-500 hover:text-gray-300"
              }`}
            >
              {cat}
            </button>
          )}
        </For>
        <button
          onClick={() => setSortDir((d) => d === "desc" ? "asc" : "desc")}
          class="ml-auto px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer"
        >
          {sortDir() === "desc" ? "↓ Newest" : "↑ Oldest"}
        </button>
      </div>

      <div class="max-h-64 overflow-y-auto overflow-x-auto">
        <table class="w-full text-[10px] font-mono min-w-[600px]">
          <thead class="bg-gray-900/50 text-gray-500 sticky top-0">
            <tr>
              <th class="text-left px-3 py-1.5 font-medium">Time</th>
              <th class="text-left px-3 py-1.5 font-medium">Type</th>
              <th class="text-left px-3 py-1.5 font-medium w-12">Dir</th>
              <th class="text-left px-3 py-1.5 font-medium">Payload</th>
              <th class="px-1 py-1.5 w-8"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800/30">
            <For each={connEvents()}>
              {(evt: any) => (
                <tr class="hover:bg-gray-900/20">
                  <td class="px-3 py-0.5 text-gray-500 whitespace-nowrap">{new Date(evt.timestamp).toLocaleTimeString()}</td>
                  <td class="px-3 py-0.5 text-cyan-400 whitespace-nowrap">{evt.original?.type ?? "?"}</td>
                  <td class="px-3 py-0.5"><DirectionIndicator channels={evt.channels} /></td>
                  <td class="px-3 py-0.5 min-w-[150px]"><ExpandablePayload payload={evt.original} /></td>
                  <td class="px-1 py-0.5"><CopyButton text={JSON.stringify(evt.original, null, 2)} /></td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </div>
  );
}
